
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FLEETRIFYROUTEPATH } from "../../routes/routesPath";

const WORD = "Fleetrify ..".split("");

export default function App() {
  const navigate = useNavigate();
  const [cycle, setCycle] = useState(0);
  const [phase, setPhase] = useState<"intro" | "Fleetrify .." | "exit">("intro");

  const totalCycles = 5;

  useEffect(() => {
    const introTimer = setTimeout(() => {
      setPhase("Fleetrify ..");
    }, 500);

    const cycleTimer = setInterval(() => {
      setCycle((prev) => {
        if (prev >= totalCycles - 1) {
          clearInterval(cycleTimer);
          setTimeout(() => setPhase("exit"), 400);
          return prev;
        }
        return prev + 1;
      });
    }, 950);

    const redirectTimer = setTimeout(() => {
      navigate(FLEETRIFYROUTEPATH.SIGN_UP);
    }, 6200);

    return () => {
      clearTimeout(introTimer);
      clearInterval(cycleTimer);
      clearTimeout(redirectTimer);
    };
  }, [navigate]);

  const progress = useMemo(() => {
    return Math.min(100, Math.round(((cycle + 1) / totalCycles) * 100));
  }, [cycle]);

  return (
    <AnimatePresence>
      {phase !== "exit" ? (
        <motion.div
          key="fleetrify-loader"
          className="fixed inset-0 overflow-hidden bg-[#0C43FF]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 1.02 }}
          transition={{ duration: 0.6, ease: "easeInOut" }}
        >
          <BlueprintBackground cycle={cycle} />

          <div className="relative z-20 flex min-h-screen flex-col items-center justify-center px-4">
            <div className="relative flex w-full max-w-[1200px] items-center justify-center">
              <BlueprintFrame cycle={cycle} />

              <div className="relative flex items-center justify-center">
                <AnimatedLoadingWord cycle={cycle} />
              </div>
            </div>

            <motion.div
              className="mt-12 flex w-full max-w-[360px] flex-col gap-2"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              <div className="h-[2px] w-full overflow-hidden rounded-full bg-white/20">
                <motion.div
                  className="h-full bg-white"
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                />
              </div>

              <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-[0.28em] text-white/75">
                <span>Initializing</span>
                <span>{progress}%</span>
              </div>
            </motion.div>
          </div>

          <FlashOverlay cycle={cycle} />
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

function BlueprintBackground({ cycle }: { cycle: number }) {
  const flash = cycle % 2 === 1;

  return (
    <>
      <motion.div
        className="absolute inset-0"
        animate={{
          backgroundColor: flash ? "#89A7FF" : "#0C43FF",
        }}
        transition={{ duration: 0.35, ease: "easeInOut" }}
      />

      <div
        className="absolute inset-0 opacity-35"
        style={{
          backgroundImage: `
            linear-gradient(rgba(7, 28, 105, 0.22) 1px, transparent 1px),
            linear-gradient(90deg, rgba(7, 28, 105, 0.22) 1px, transparent 1px)
          `,
          backgroundSize: "64px 64px",
        }}
      />

      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)
          `,
          backgroundSize: "160px 160px",
        }}
      />

      <motion.div
        className="absolute inset-0"
        animate={{ opacity: flash ? 0.06 : 0.12 }}
        transition={{ duration: 0.35 }}
        style={{
          background:
            "radial-gradient(circle at center, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.04) 28%, transparent 72%)",
        }}
      />
    </>
  );
}

function BlueprintFrame({ cycle }: { cycle: number }) {
  const flash = cycle % 2 === 1;

  return (
    <div className="pointer-events-none absolute inset-0 flex items-center justify-center px-4">
      <motion.div
        className="relative h-[220px] w-full max-w-[980px] md:h-[280px] lg:h-[320px]"
        animate={{
          scale: flash ? 1.015 : 1,
          opacity: flash ? 0.9 : 1,
        }}
        transition={{ duration: 0.35 }}
      >
        <div className="absolute inset-0 border border-[#173D9A]/70" />

        <motion.div
          className="absolute left-[4%] top-[8%] h-[84%] w-[32%] border border-[#173D9A]/70"
          animate={{
            x: cycle % 2 === 0 ? 0 : 6,
            skewY: cycle % 2 === 0 ? 0 : -1.5,
          }}
          transition={{ duration: 0.45 }}
        />

        <motion.div
          className="absolute left-[31%] top-[10%] h-[82%] w-[37%] border border-[#173D9A]/70"
          animate={{
            x: cycle % 2 === 0 ? 0 : 10,
            skewY: cycle % 2 === 0 ? 0 : -2.5,
          }}
          transition={{ duration: 0.45 }}
        />

        <motion.div
          className="absolute right-[7%] top-[12%] h-[78%] w-[24%] border border-[#173D9A]/70"
          animate={{
            x: cycle % 2 === 0 ? 0 : -4,
            skewY: cycle % 2 === 0 ? 0 : 1.5,
          }}
          transition={{ duration: 0.45 }}
        />

        <div
          className="absolute inset-0 opacity-60"
          style={{
            backgroundImage: `
              linear-gradient(90deg, rgba(10,33,112,0.38) 1px, transparent 1px)
            `,
            backgroundSize: "34px 34px",
          }}
        />
      </motion.div>
    </div>
  );
}

function AnimatedLoadingWord({ cycle }: { cycle: number }) {
  const flash = cycle % 2 === 1;

  return (
    <motion.div
      className="relative flex items-end"
      animate={{ scale: flash ? 1.015 : 1 }}
      transition={{ duration: 0.35 }}
    >
      {WORD.map((letter, index) => {
        const isLast = index >= 6;
        const isSecondToLast = index === 6;

        return (
          <motion.span
            key={`${letter}-${index}-${cycle}`}
            className="relative inline-block select-none font-black leading-none text-white"
            style={{
              fontSize: "clamp(3rem, 11vw, 8.5rem)",
              letterSpacing: "-0.06em",
            }}
            initial={{
              opacity: 0,
              y: 22,
              filter: "blur(8px)",
            }}
            animate={{
              opacity: 1,
              y: 0,
              x:
                isLast && cycle % 2 === 0
                  ? -10
                  : isLast && cycle % 2 === 1
                    ? 12
                    : isSecondToLast && cycle % 2 === 0
                      ? -4
                      : isSecondToLast && cycle % 2 === 1
                        ? 6
                        : 0,
              filter: flash && isLast ? "blur(2px)" : "blur(0px)",
            }}
            transition={{
              duration: 0.45,
              delay: index * 0.03,
              ease: [0.2, 0.8, 0.2, 1],
            }}
          >
            {letter}

            {(isLast || isSecondToLast) && (
              <motion.span
                className="absolute inset-0 text-white/25"
                animate={{
                  x: isLast ? 22 : 12,
                  opacity: [0.15, 0.35, 0.15],
                }}
                transition={{
                  duration: 0.75,
                  ease: "easeInOut",
                }}
              >
                {letter}
              </motion.span>
            )}
          </motion.span>
        );
      })}
    </motion.div>
  );
}

function FlashOverlay({ cycle }: { cycle: number }) {
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={cycle}
        className="pointer-events-none absolute inset-0 z-10"
        initial={{ opacity: 0.18 }}
        animate={{ opacity: 0 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.35, ease: "easeOut" }}
        style={{
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.06) 30%, transparent 75%)",
        }}
      />
    </AnimatePresence>
  );
}







// // src/App.tsx
// import { useEffect, useState } from "react";
// import { useNavigate } from "react-router-dom";
// import { motion, AnimatePresence } from "framer-motion";
// import { FLEETRIFYROUTEPATH } from "../../routes/routesPath";
// import logo from "../../assets/icons/FleetrifyLogo.svg";

// export default function App() {
//   const navigate = useNavigate();
//   const [phase, setPhase] = useState<"loading" | "reveal" | "done">("loading");
//   const [progress, setProgress] = useState(0);
//   const [currentWord, setCurrentWord] = useState(0);

//   const words = ["FLEET", "TRACK", "MANAGE", "MOVE", "FLEETRIFY"];

//   useEffect(() => {
//     // progress bar
//     const interval = setInterval(() => {
//       setProgress((p) => {
//         if (p >= 100) {
//           clearInterval(interval);
//           return 100;
//         }
//         return p + 2;
//       });
//     }, 40);

//     // cycle words
//     const wordInterval = setInterval(() => {
//       setCurrentWord((w) => (w + 1) % words.length);
//     }, 400);

//     // move to reveal phase
//     const revealTimer = setTimeout(() => {
//       clearInterval(wordInterval);
//       setCurrentWord(4);
//       setPhase("reveal");
//     }, 2200);

//     // redirect
//     const redirectTimer = setTimeout(() => {
//       setPhase("done");
//       navigate(FLEETRIFYROUTEPATH.SIGN_UP);
//     }, 4000);

//     return () => {
//       clearInterval(interval);
//       clearInterval(wordInterval);
//       clearTimeout(revealTimer);
//       clearTimeout(redirectTimer);
//     };
//   }, [navigate]);

//   return (
//     <AnimatePresence>
//       {phase !== "done" && (
//         <motion.div
//           key="loader"
//           className="fixed inset-0 flex flex-col items-center justify-center overflow-hidden"
//           style={{ background: "#00032A" }}
//           exit={{ opacity: 0, scale: 1.05 }}
//           transition={{ duration: 0.6, ease: "easeInOut" }}
//         >
//           {/* animated background grid */}
//           <div
//             className="absolute inset-0 opacity-10"
//             style={{
//               backgroundImage: `linear-gradient(#143887 1px, transparent 1px), linear-gradient(90deg, #143887 1px, transparent 1px)`,
//               backgroundSize: "60px 60px",
//             }}
//           />

//           {/* radial glow */}
//           <motion.div
//             className="absolute rounded-full"
//             style={{
//               width: 600,
//               height: 600,
//               background: "radial-gradient(circle, rgba(20,56,135,0.4) 0%, transparent 70%)",
//             }}
//             animate={{ scale: [1, 1.2, 1], opacity: [0.4, 0.7, 0.4] }}
//             transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
//           />

//           {/* orbiting ring */}
//           <motion.div
//             className="absolute border border-blue-700 rounded-full opacity-20"
//             style={{ width: 340, height: 340 }}
//             animate={{ rotate: 360 }}
//             transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
//           />
//           <motion.div
//             className="absolute border border-blue-500 rounded-full opacity-10"
//             style={{ width: 500, height: 500 }}
//             animate={{ rotate: -360 }}
//             transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
//           />

//           {/* ── LOGO ── */}
//           <motion.div
//             className="mb-8 z-10"
//             animate={{ scale: [1, 1.08, 1] }}
//             transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
//           >
//             <div
//               className="w-20 h-20 rounded-2xl flex items-center justify-center"
//               style={{
//                 background: "rgba(255,255,255,0.08)",
//                 border: "1px solid rgba(255,255,255,0.15)",
//                 boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
//               }}
//             >
//               <img src={logo} alt="Fleetrify" className="w-12 h-12 object-contain" />
//             </div>
//           </motion.div>

//           {/* word cycler */}
//           <div className="z-10 h-20 flex items-center justify-center mb-4 overflow-hidden">
//             <AnimatePresence mode="wait">
//               <motion.h1
//                 key={currentWord}
//                 initial={{ y: 40, opacity: 0, filter: "blur(8px)" }}
//                 animate={{ y: 0, opacity: 1, filter: "blur(0px)" }}
//                 exit={{ y: -40, opacity: 0, filter: "blur(8px)" }}
//                 transition={{ duration: 0.25, ease: "easeOut" }}
//                 className="text-5xl font-black tracking-widest"
//                 style={{
//                   fontFamily: "'Courier New', monospace",
//                   color: currentWord === 4 ? "#FFCD03" : "white",
//                   letterSpacing: "0.15em",
//                 }}
//               >
//                 {words[currentWord]}
//               </motion.h1>
//             </AnimatePresence>
//           </div>

//           {/* tagline */}
//           {phase === "reveal" && (
//             <motion.p
//               initial={{ opacity: 0, y: 10 }}
//               animate={{ opacity: 1, y: 0 }}
//               transition={{ delay: 0.3 }}
//               className="text-blue-300 text-sm tracking-widest uppercase mb-10 z-10"
//               style={{ fontFamily: "'Courier New', monospace" }}
//             >
//              Connecting Fleets To Users With Precition..
//             </motion.p>
//           )}

//           {/* progress bar */}
//           <div className="z-10 w-64 mt-8">
//             <div className="h-px bg-blue-900 w-full rounded-full overflow-hidden">
//               <motion.div
//                 className="h-full rounded-full"
//                 style={{
//                   background: "linear-gradient(90deg, #143887, #FFCD03)",
//                   width: `${progress}%`,
//                 }}
//                 transition={{ ease: "linear" }}
//               />
//             </div>
//             <div className="flex justify-between mt-2">
//               <span
//                 className="text-blue-600 text-xs"
//                 style={{ fontFamily: "monospace" }}
//               >
//                 INITIALIZING
//               </span>
//               <span
//                 className="text-blue-400 text-xs"
//                 style={{ fontFamily: "monospace" }}
//               >
//                 {progress}%
//               </span>
//             </div>
//           </div>

//           {/* corner decorations */}
//           <div className="absolute top-6 left-6 w-8 h-8 border-t-2 border-l-2 border-blue-700 opacity-40" />
//           <div className="absolute top-6 right-6 w-8 h-8 border-t-2 border-r-2 border-blue-700 opacity-40" />
//           <div className="absolute bottom-6 left-6 w-8 h-8 border-b-2 border-l-2 border-blue-700 opacity-40" />
//           <div className="absolute bottom-6 right-6 w-8 h-8 border-b-2 border-r-2 border-blue-700 opacity-40" />

//           {/* bottom dots */}
//           <div className="absolute bottom-10 flex gap-2 z-10">
//             {[0, 1, 2].map((i) => (
//               <motion.div
//                 key={i}
//                 className="w-1.5 h-1.5 rounded-full bg-blue-500"
//                 animate={{ opacity: [0.2, 1, 0.2] }}
//                 transition={{ duration: 1, repeat: Infinity, delay: i * 0.3 }}
//               />
//             ))}
//           </div>
//         </motion.div>
//       )}
//     </AnimatePresence>
//   );
// }